import { Icon, ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { Pressable, PressableProps } from 'components/Pressable';
import {
  TEXT_SIZE_NORMAL,
  TEXT_SIZE_XSMALL,
  Txt,
  TxtPreset,
  TxtProps,
} from 'components/Txt';
import { useNavigationIsSelected } from 'hooks/useNavigationIsSelected';
import { theme } from 'lib/theme';
import { noop } from 'lodash';
import React from 'react';
import {
  ImageStyle,
  Platform,
  GestureResponderEvent,
  StyleProp,
  StyleSheet,
  Switch,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import { FIELD_ICON_SIZE } from 'types/types';
import Animated, {
  cancelAnimation,
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import { FieldTypeIcon } from './Field/FieldTypeIcon';
import { Menu, MenuProps } from './Menu';
import { PressableWithMenu } from './PressableWithContext';

import { ViewWithSeparators } from './ViewWithSeparators';
import { TxtAsterisk } from './TxtAsterisk';

export type CardRowProps = {
  navigationType?:
    | 'explorer'
    | 'modal'
    | 'screen'
    | 'date-picker'
    | 'time-picker'
    | 'input-text'
    | 'input-number'
    | 'input-check'
    | 'input-radio'
    | 'add'
    | 'pin'
    | 'qr'
    | 'none';
  checkedValue?: boolean;
  checkColor?: string;
  radioValue?: boolean;
  switchValue?: boolean;
  switchThumbColor?: string;
  switchActiveThumbColor?: string;
  onSwitchChange?: () => void;
  icon?: ICONS;
  iconSize?: number;
  iconColor?: string;
  iconStyle?: StyleProp<ImageStyle>;
  iconPressableStyle?: StyleProp<ViewStyle>;
  iconRotate?: boolean;
  childrenStyle?: StyleProp<ViewStyle>;
  selected?: boolean;
  onPress?: () => void;
  onPressIn?: () => void;
  onDrag?: () => void;
  onAdd?: () => void;
  onActionLeft?: null | ((event: GestureResponderEvent) => void);
  actionIconLeft?: ICONS;
  onAction?: null | ((event: GestureResponderEvent) => void);
  actionIcon?: ICONS;
  onAction2?: null | ((event: GestureResponderEvent) => void);
  actionIcon2?: ICONS;

  style?: StyleProp<ViewStyle>;
  hoveredStyle?: StyleProp<ViewStyle>;
  labelStyle?: StyleProp<TextStyle>;
  label2Style?: StyleProp<TextStyle>;
  labelContainerStyle?: StyleProp<ViewStyle>;
  valueStyle?: StyleProp<TextStyle>;
  contentStyle?: StyleProp<ViewStyle>;

  label?: string;
  labelSize?: TxtProps['size'];
  labelColor?: string;
  labelWeight?: TxtProps['weight'];
  labelNumberOfLines?: number;
  labelAlignTop?: boolean;

  label2?: string;
  label2Size?: TxtProps['size'];
  label2Color?: string;
  label2NumberOfLines?: number;

  value?: string;
  valueColor?: string;
  valueNumberOfLines?: number;
  valueNumberOfLinesWithLabel?: number;

  placeholder?: string;

  disabled?: boolean;
  navigationIcon?: ICONS;
  navigationIconColor?: string;
  navigationIconSize?: number;
  onIconPress?: () => void;

  contentLeft?: React.ReactNode;
  indentActions?: boolean;
  smallActionGap?: boolean;

  menuOptions?: MenuProps['options'];

  /**
   * Show menu on press if onPress is undefined
   */
  showMenuOnPress?: boolean;
  showMenuOnContext?: boolean;

  hideMenuButton?: boolean;

  children?: React.ReactNode;
  rbs?: boolean;
  showBadge?: boolean;

  navigationIsSelected?: {
    name: string;
    params: Record<string, unknown>;
  };

  compact?: boolean;
  small?: boolean;
  required?: boolean;

  accessibilityHint?: PressableProps['accessibilityHint'];
  accessibilityLabel?: PressableProps['accessibilityHint'];
};

export const CardRow: React.FC<CardRowProps> = ({
  actionIconLeft = ICONS.HandPointerLight,
  actionIcon = ICONS.HandPointerLight,
  actionIcon2 = ICONS.HandPointerLight,
  checkedValue,
  checkColor,
  children,

  childrenStyle,
  contentLeft,
  contentStyle,
  disabled,
  icon,
  iconColor,
  iconPressableStyle,
  iconRotate,
  iconSize = 19,
  iconStyle,
  label,
  required,
  labelSize,
  label2Size,
  labelWeight,
  label2,
  labelNumberOfLines = 1,
  label2NumberOfLines = undefined,
  labelAlignTop,
  labelColor,
  label2Color,
  labelContainerStyle,
  labelStyle,
  label2Style,
  navigationIcon,
  navigationIconColor = theme.colors.placeholder,
  navigationIconSize = 19,
  navigationIsSelected,
  navigationType = 'none',
  onActionLeft,
  onAction,
  onAction2,
  onAdd,
  onDrag,
  onIconPress,
  onPress,
  onPressIn,
  onSwitchChange,
  placeholder,
  radioValue,
  selected,
  style,
  hoveredStyle,
  switchValue,
  switchThumbColor = 'white',
  switchActiveThumbColor = theme.colors.blue,
  value,
  valueColor,
  valueNumberOfLines,
  valueNumberOfLinesWithLabel = 1,
  valueStyle,
  indentActions,
  smallActionGap = false,
  menuOptions,
  showMenuOnPress: menuOnPress,
  showMenuOnContext = true,
  hideMenuButton,
  rbs,
  showBadge,
  compact = false,
  small = false,
  accessibilityHint,
  accessibilityLabel,
}) => {
  const isSelected =
    useNavigationIsSelected(
      navigationIsSelected?.name,
      navigationIsSelected?.params,
    ) ||
    selected ||
    false;

  let finalNavigationIconSize = navigationIconSize;
  let finalNavigationIcon: ICONS | undefined = navigationIcon;
  if (!finalNavigationIcon) {
    if (navigationType === 'add') {
      finalNavigationIcon = ICONS.PlusLight;
    } else if (navigationType === 'screen') {
      finalNavigationIcon = ICONS.ChevronRightSolid;
      finalNavigationIconSize = 12;
    } else if (navigationType === 'input-text') {
      finalNavigationIcon = ICONS.InputTextDuo;
    } else if (navigationType === 'input-number') {
      finalNavigationIcon = ICONS.InputNumberDuo;
      finalNavigationIconSize = FIELD_ICON_SIZE;
    } else if (navigationType === 'input-check') {
      finalNavigationIcon = ICONS.ListCheckLight;
    } else if (navigationType === 'input-radio') {
      finalNavigationIcon = ICONS.ListRadioLight;
    } else if (navigationType === 'date-picker') {
      finalNavigationIcon = ICONS.CalendarDayLight;
    } else if (navigationType === 'time-picker') {
      finalNavigationIcon = ICONS.ClockLight;
    } else if (navigationType === 'pin') {
      finalNavigationIcon = ICONS.LocationPinLight;
    } else if (navigationType === 'qr') {
      finalNavigationIcon = ICONS.QRCodeLight;
    } else if (navigationType === 'modal') {
      finalNavigationIcon = undefined;
    } else if (navigationType === 'explorer') {
      finalNavigationIcon = undefined;
    }
  }

  const handleOnPress = disabled ? undefined : onPress || onSwitchChange;
  const finalLabelAlignTop = labelAlignTop || !label;
  const isInteractable =
    onPress ||
    switchValue !== undefined ||
    (menuOnPress && !!menuOptions?.length);

  const rotationRef = useSharedValue(0);
  React.useEffect(() => {
    if (iconRotate) {
      rotationRef.value = withRepeat(
        withTiming(1, { duration: 1000, easing: Easing.linear }),
        0,
      );
    }
    return () => {
      cancelAnimation(rotationRef);
    };
  }, [iconRotate, rotationRef]);

  const animatedStyles = useAnimatedStyle(() => {
    return {
      transform: [
        {
          rotate: `${interpolate(rotationRef.value, [0, 1], [0, 360])}deg`,
        },
      ],
    };
  });

  const finalValueColour =
    valueColor || (label ? theme.colors.fieldValueText : theme.colors.black);

  const RightMemo = React.useMemo(
    () =>
      (switchValue !== undefined ||
        onAction2 !== undefined ||
        onAction !== undefined ||
        (menuOptions?.length && !hideMenuButton) ||
        onDrag ||
        finalNavigationIcon) && (
        <ViewWithSeparators
          horizontal
          separatorMargin={4}
          style={{
            marginRight: indentActions
              ? 0
              : smallActionGap
                ? -theme.padding
                : -theme.padding / 2,
            marginLeft: smallActionGap ? 0 : 4,
            alignItems: 'center',
            justifyContent: 'center',
            alignSelf: 'stretch',
          }}
        >
          {switchValue !== undefined && (
            <Pressable
              onPress={noop}
              style={{
                marginRight: theme.buttonMargin,
                marginLeft: theme.buttonMargin,
              }}
            >
              <Switch
                value={switchValue}
                thumbColor={
                  switchValue ? switchActiveThumbColor : switchThumbColor
                }
                {...(Platform.OS === 'web'
                  ? { activeThumbColor: switchActiveThumbColor }
                  : {})}
                trackColor={
                  Platform.OS === 'ios'
                    ? {
                        false: theme.colors.blue20,
                        true: theme.colors.blue,
                      }
                    : {
                        false: theme.colors.black20,
                        true: theme.colors.blue20,
                      }
                }
                onValueChange={onSwitchChange}
              />
            </Pressable>
          )}
          {onAction2 !== undefined && (
            <IconButton
              icon={actionIcon2}
              onPress={onAction2 || undefined}
              disabled={onAction2 === null}
              color={onAction2 ? undefined : theme.colors.black80}
              glass={false}
            />
          )}
          {onAction !== undefined && (
            <IconButton
              icon={actionIcon}
              onPress={onAction || undefined}
              disabled={onAction === null}
              glass={false}
              color={onAction ? undefined : theme.colors.black80}
            />
          )}
          {!hideMenuButton && !!menuOptions?.length && (
            <Menu
              options={menuOptions}
              iconButtonConfig={{ icon: ICONS.EllipsisLight, glass: false }}
            />
          )}
          {onDrag && (
            <Pressable
              onPressIn={onDrag}
              pointerEvents="box-only"
              style={{
                alignSelf: 'stretch',
                justifyContent: 'center',
                alignItems: 'center',
                width: theme.grid * 4,
              }}
            >
              <Icon source={ICONS.GripLinesLight} />
            </Pressable>
          )}
          {finalNavigationIcon && (
            <FieldTypeIcon
              icon={finalNavigationIcon}
              iconSize={finalNavigationIconSize}
              iconColor={navigationIconColor}
            />
          )}
        </ViewWithSeparators>
      ),
    [
      actionIcon,
      actionIcon2,
      finalNavigationIcon,
      finalNavigationIconSize,
      hideMenuButton,
      indentActions,
      menuOptions,
      navigationIconColor,
      onAction,
      onAction2,
      onDrag,
      onSwitchChange,
      smallActionGap,
      switchValue,
      switchThumbColor,
      switchActiveThumbColor,
    ],
  );

  return (
    <PressableWithMenu
      menuOptions={menuOptions}
      menuOnPress={menuOnPress}
      onPress={isSelected ? noop : handleOnPress}
      onPressIn={onPressIn}
      showMenuOnContext={showMenuOnContext}
      hoveredStyle={
        !isSelected ? [styles.pressableHovered, hoveredStyle] : undefined
      }
      accessibilityHint={accessibilityHint}
      accessibilityLabel={accessibilityLabel}
      pressedStyle={!isSelected ? styles.pressablePressed : undefined}
      style={[
        { position: 'relative' },
        compact ? styles.pressableCompact : styles.pressable,
        !isInteractable ? styles.notPressable : undefined,
        isSelected ? styles.pressableSelected : null,
        disabled ? styles.disabled : null,
        style,
      ]}
    >
      {rbs && (
        <View
          style={{
            backgroundColor: theme.colors.support30,
            width: 4,
            height: 4,
            borderRadius: 2,
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 9,
          }}
        />
      )}
      {showBadge && (
        <View
          style={{
            position: 'absolute',
            right: 4,
            bottom: 4,
            width: 8,
            height: 8,
            borderRadius: 4,
            backgroundColor: theme.colors.orange,
            zIndex: 1,
            elevation: 1,
          }}
        />
      )}
      {onActionLeft !== undefined && (
        <IconButton
          icon={actionIconLeft}
          size={31}
          onPress={onActionLeft || undefined}
          disabled={onActionLeft === null}
          style={styles.actionIconLeft}
          glass={false}
        />
      )}
      {onAdd && (
        <Pressable style={styles.iconLeftPressable} onPress={onAdd}>
          <Icon source={ICONS.PlusLight} size={19} color={theme.colors.blue} />
        </Pressable>
      )}
      {checkedValue !== undefined ? (
        <Pressable style={styles.iconLeftPressable} onPress={onIconPress}>
          <Icon
            source={checkedValue ? ICONS.SquareCheckSolid : ICONS.SquareLight}
            size={19}
            color={checkColor || (checkedValue ? theme.colors.blue : undefined)}
          />
        </Pressable>
      ) : radioValue !== undefined ? (
        <Pressable style={styles.iconLeftPressable} onPress={onIconPress}>
          <Icon
            source={radioValue ? ICONS.CircleDotSolid : ICONS.CircleLight}
            size={19}
            color={radioValue ? theme.colors.blue : undefined}
          />
        </Pressable>
      ) : null}
      {icon && (
        <Pressable
          style={[styles.iconLeftPressable, iconPressableStyle]}
          onPress={onIconPress}
        >
          {iconRotate ? (
            <Animated.View style={animatedStyles}>
              <Icon
                source={icon}
                size={iconSize}
                color={iconColor}
                style={iconStyle}
              />
            </Animated.View>
          ) : (
            <Icon
              source={icon}
              size={iconSize}
              color={iconColor}
              style={iconStyle}
            />
          )}
        </Pressable>
      )}
      {contentLeft}
      <View
        style={[
          finalLabelAlignTop
            ? compact
              ? styles.contentLabelTopCompact
              : styles.contentLabelTop
            : compact
              ? styles.contentLabelLeftCompact
              : styles.contentLabelLeft,
          contentStyle,
        ]}
      >
        {!!label && (
          <View
            style={[
              finalLabelAlignTop ? styles.labelAlignTop : styles.labelAlignLeft,
              labelContainerStyle,
            ]}
          >
            <Txt
              numberOfLines={labelNumberOfLines}
              size={labelSize}
              preset={TxtPreset.CardRowLabel}
              presetSmall={small}
              weight={labelWeight}
              style={[
                styles.label,
                labelColor ? { color: labelColor } : undefined,
                labelStyle,
              ]}
            >
              {label}
              {required && <TxtAsterisk />}
            </Txt>
            {!!label2 && (
              <Txt
                numberOfLines={label2NumberOfLines}
                preset={TxtPreset.CardRowLabel2}
                presetSmall={small}
                size={label2Size}
                style={[
                  label2Style,
                  label2Color ? { color: label2Color } : undefined,
                ]}
              >
                {label2}
              </Txt>
            )}
          </View>
        )}
        {children ? (
          <View style={childrenStyle}>{children}</View>
        ) : value ? (
          <Txt
            style={[
              finalLabelAlignTop ? styles.valueLabelTop : styles.valueLabelLeft,
              { color: finalValueColour },
              valueStyle,
            ]}
            preset={
              finalLabelAlignTop
                ? label
                  ? TxtPreset.CardRowValueUnder
                  : TxtPreset.CardRowValueUnderNoLabel
                : label
                  ? TxtPreset.CardRowValueInline
                  : TxtPreset.CardRowValueInlineNoLabel
            }
            presetSmall={small}
            numberOfLines={
              finalLabelAlignTop || !label
                ? valueNumberOfLines
                : valueNumberOfLinesWithLabel
            }
          >
            {value}
          </Txt>
        ) : placeholder ? (
          <Txt
            style={[
              finalLabelAlignTop ? styles.valueLabelTop : styles.valueLabelLeft,
              finalLabelAlignTop && !!label
                ? styles.valueSizeSmall
                : styles.valueSizeNormal,
              { color: theme.colors.placeholder },
              valueStyle,
            ]}
            numberOfLines={
              finalLabelAlignTop || !label
                ? valueNumberOfLines
                : valueNumberOfLinesWithLabel
            }
            preset={
              finalLabelAlignTop
                ? TxtPreset.CardRowValueUnder
                : TxtPreset.CardRowValueInline
            }
            presetSmall={small}
          >
            {placeholder}
          </Txt>
        ) : null}
      </View>
      {RightMemo}
    </PressableWithMenu>
  );
};

const styles = StyleSheet.create({
  contentLabelLeft: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    paddingVertical: theme.padding,
  },

  contentLabelLeftCompact: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    paddingVertical: theme.padding / 2,
  },

  contentLabelTop: {
    flex: 1,
    paddingVertical: theme.grid,
  },

  contentLabelTopCompact: {
    flex: 1,
    paddingTop: theme.paddingHorizontalHalf,
    paddingBottom: theme.paddingHorizontalHalf,
  },

  disabled: {
    opacity: 0.42,
  },
  actionIconLeft: {
    marginLeft: -theme.padding / 2,
    marginRight: theme.padding / 2,
  },
  iconLeftPressable: {
    paddingHorizontal: theme.padding / 2,
    paddingVertical: theme.padding / 2,
    marginLeft: -(theme.padding / 2),
    marginRight: theme.padding / 2,
    borderRadius: 4,
  },
  label: {
    color: theme.colors.text,
  },
  labelAlignLeft: {
    flexGrow: 1,
    flexShrink: 1,
  },
  labelAlignTop: {},
  notPressable: {
    backgroundColor: '#ffffff80',
  },
  pressable: {
    alignItems: 'center',
    backgroundColor: 'white',
    flexDirection: 'row',
    minHeight: 50,
    paddingLeft: theme.padding,
    paddingRight: theme.padding,
  },
  pressableCompact: {
    alignItems: 'center',
    backgroundColor: 'white',
    flexDirection: 'row',
    minHeight: 43,
    paddingLeft: theme.padding,
    paddingRight: theme.padding,
  },
  pressableHovered: {
    backgroundColor: '#fafafa',
  },
  pressablePressed: {
    backgroundColor: '#dddddd',
  },
  pressableSelected: {
    backgroundColor: theme.colors.blue10,
  },
  valueLabelLeft: {
    flexBasis: 'auto',
    flexGrow: 0,
    flexShrink: 100,
    marginLeft: theme.padding,
    minWidth: 25,
    textAlign: 'right',
  },
  valueLabelTop: {},
  valueSizeNormal: {
    fontSize: TEXT_SIZE_NORMAL,
  },
  valueSizeSmall: {
    fontSize: TEXT_SIZE_XSMALL,
  },
});
